import { useMemo } from 'react';
import { MealPlanDisplayFoodItemFragment, MealPlanShefFragment } from 'src/gqlReactTypings.generated.d';
import { getQuantityInUnits } from 'src/pages/consumer/meal-plans/utils/cartServingsConversion';
import { useHistory } from 'src/shared/hooks/useHistory';
import { Routes } from 'src/shared/Routes';
import { locationInRoutes } from 'src/shared/utils/RouteUtilities';
import { useCartStoreSelectors } from 'src/ztore/cart-store';
import { getFoodItemAvailabilityForCarts } from './foodItemAvailabilityMath';

/**
 * Calculates the number of available units for a food item, taking into account the
 * number of servings per meal for mains.
 *
 * When a user is editing an existing order, we need to add the number of servings in the
 * pre-edit cart to the shef and food item availability because those servings are taking up
 * availability slots on the shef and the food item.
 *
 * @param foodItem - The food item to check availability for.
 * @param shef - The shef to check availability for.
 * @returns
 * - numAvailableUnits: The number of available units for the food item and shef, whichever is less.
 * - numUnitsInCart: The number of units in the cart for the food item.
 * - numAvailableServings: The number of available servings (un-floored) for the food item and shef, whichever is less.
 * - numServingsInCart: The number of servings in the cart for the food item.
 */
export const useFoodItemAvailability = (foodItem: MealPlanDisplayFoodItemFragment, shef: MealPlanShefFragment) => {
  const { getCartForShef, getPreEditCartForShef, deliveryDate, numServings } = useCartStoreSelectors();
  const { id: shefId } = shef;
  const cartForShef = getCartForShef(shefId);
  const preEditCartForShef = getPreEditCartForShef(shefId);
  const history = useHistory();
  const isEditMode = locationInRoutes(history.location, [Routes.CONSUMER_MEAL_PLANS_EDIT_ORDER]);

  return useMemo(() => {
    const cartItemForFoodItem = cartForShef?.cartItems.find((item) => item.foodItem.id === foodItem.id);
    const { numAvailableUnits, numAvailableServings } = getFoodItemAvailabilityForCarts({
      foodItem,
      shef,
      deliveryDate,
      numServings,
      cartForShef,
      preEditCartForShef,
      isEditMode,
    });

    return {
      numAvailableUnits,
      numUnitsInCart: cartItemForFoodItem ? getQuantityInUnits(cartItemForFoodItem, numServings) : 0,
      numAvailableServings,
      numServingsInCart: cartItemForFoodItem?.quantity ?? 0,
    };
  }, [cartForShef, preEditCartForShef, deliveryDate, numServings, foodItem, shef, isEditMode]);
};
